import { getAlpha3CountryCode } from './country-codes.js';

// When billing/shipping differ, seed the "visual" shipping block shown in the
// UI from the user + billing values so the shipping fields start populated.
export const visualShipping = (form) => {
	if (form.shipping_same === false) {
		form.visual_shipping = {
			first_name: '',
			last_name: '',
			name: form.user.name,
			phone: form.user.phone,
			company_name: '',
			country: form.billing_address.country,
			address: form.billing_address.address,
			address2: form.billing_address.address2,
			city: form.billing_address.city,
			state: form.billing_address.state,
			postal_code: form.billing_address.postal_code,
		};
	}
	return form;
};

// Normalize the checkout form into the shape the backend expects: split the
// display name into first/last (without clobbering Apple Pay data), strip
// phone formatting, convert country codes to alpha-3, and resolve shipping
// from billing when "ship to billing" is selected. Operates on a deep copy so
// the reactive form bound to the UI is never mutated.
export const remapForm = (form) => {
	const formCopy = JSON.parse(JSON.stringify(form));

	try {
		// Only extract first_name and last_name from user.name if they're not already set.
		// This preserves Apple Pay address data that already has first_name and last_name
		if ((!formCopy.billing_address.first_name || !formCopy.billing_address.last_name) && formCopy.user.name) {
			let names = formCopy.user.name.split(' ');

			if (!formCopy.billing_address.first_name) {
				if (names.length > 0) {
					formCopy.billing_address.first_name = names.shift();
				} else {
					formCopy.billing_address.first_name = formCopy.user.name;
				}
			}
			if (!formCopy.billing_address.last_name && names.length > 0) {
				formCopy.billing_address.last_name = names.join(' ');
			}
		}

		// Ensure first_name and last_name are set, even if empty strings
		formCopy.billing_address.first_name = formCopy.billing_address.first_name || '';
		formCopy.billing_address.last_name = formCopy.billing_address.last_name || '';

		formCopy.user.phone = (formCopy.user.phone || '').replace(/[()-\s]/g, '');
		formCopy.billing_address.phone_number = formCopy.user.phone;
		formCopy.billing_address.phone = formCopy.user.phone;

		// Klaviyo SMS guard: the backend's formatPhoneNumber() returns null for
		// empty/unparseable phones, which then crashes the strict-typed
		// SendKlaviyoSMSCheckoutSubscriptionJob constructor (Argument #2 ($phone)
		// must be of type string, null given). Strip isSubscribed from the
		// outgoing payload when phone can't be parsed (the visible checkbox state
		// on-screen is preserved -- this only affects the request body).
		const _phoneDigits = (formCopy.user.phone || '').replace(/\D/g, '');
		if (formCopy.user && _phoneDigits.length < 10) {
			formCopy.user.isSubscribed = false;
		}

		// Convert alpha-2 country codes to alpha-3 for backend compatibility
		formCopy.billing_address.country = getAlpha3CountryCode(formCopy.billing_address.country);

		if (formCopy.visual_shipping && formCopy.visual_shipping.country) {
			formCopy.visual_shipping.country = getAlpha3CountryCode(formCopy.visual_shipping.country);
		}
	} catch (e) {
		console.error('Error in remapForm:', e);
		throw e;
	}

	const name = formCopy.shipping_address.name || formCopy.user.name;
	// Only extract first_name and last_name from shipping_address.name if they're not already set.
	// This preserves Apple Pay address data that already has first_name and last_name
	if ((!formCopy.shipping_address.first_name || !formCopy.shipping_address.last_name) && name) {
		let shippingNames = name.split(' ');
		if (!formCopy.shipping_address.first_name) {
			if (shippingNames.length > 0) {
				formCopy.shipping_address.first_name = shippingNames.shift();
			} else {
				formCopy.shipping_address.first_name = name;
			}
		}
		if (!formCopy.shipping_address.last_name && shippingNames.length > 0) {
			formCopy.shipping_address.last_name = shippingNames.join(' ');
		}
	}

	// Ensure first_name and last_name are set, even if empty strings
	formCopy.shipping_address.first_name = formCopy.shipping_address.first_name || '';
	formCopy.shipping_address.last_name = formCopy.shipping_address.last_name || '';

	if (formCopy.shipping_same === false) {
		formCopy.shipping_address.phone = (formCopy.shipping_address.phone || '').replace(/[()-\s]/g, '');
		formCopy.shipping_address.phone_number = formCopy.shipping_address.phone;

		// Convert alpha-2 country codes to alpha-3 for shipping address
		formCopy.shipping_address.country = getAlpha3CountryCode(formCopy.shipping_address.country);
	} else {
		formCopy.shipping_address = formCopy.billing_address;
		formCopy.shipping_address.phone_number = formCopy.billing_address.phone_number;
		formCopy.shipping_address.phone = formCopy.billing_address.phone_number;
	}

	return formCopy;
};
